import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { useLoginMutation } from '@app/store/slices/auth/authApi';
import {
  useLazyGetCurrentUserQuery,
  useUpdateLanguageMutation,
} from '@app/store/slices/users/usersApi';
import notification from '@app/shared/utils/notification';
import { LANGUAGES } from '@app/shared/constants/common';
import { LoginMethod } from '@app/shared/types/common';
import { LoginRequest } from '@app/store/slices/auth/authTypes';
import { LoginFormSchema } from '@app/pages/AuthPage';
import { setAccessToken } from '@/app/store/slices/auth/authSlice';

const DEFAULT_FORM_VALUES: LoginFormSchema = { telegramId: '', email: '', password: '' };

export const useLogin = () => {
  const { t, i18n } = useTranslation();
  const { handleFetchError } = useApiErrorHandler();
  const dispatch = useDispatch();

  const [login, { isLoading: isLoggingIn }] = useLoginMutation();
  const [getCurrentUser] = useLazyGetCurrentUserQuery();
  const [updateLanguage] = useUpdateLanguageMutation();

  const [loginMethod, setLoginMethod] = useState<LoginMethod>(LoginMethod.TELEGRAM);

  const form = useForm<LoginFormSchema>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const handleLoginSubmit = async (body: LoginFormSchema) => {
    try {
      if (loginMethod === LoginMethod.TELEGRAM) {
        const telegramPayload: LoginRequest = {
          telegramId: body.telegramId ?? '',
          password: body.password,
        };

        const res = await login(telegramPayload).unwrap();
        if (res.data?.accessToken) {
          dispatch(setAccessToken(res.data.accessToken));
        }
      } else /* if (loginMethod === LoginMethod.EMAIL) */ {
        notification.info(t('auth.errors.email_unavailable'));
        return;
      }

      const user = await getCurrentUser().unwrap();
      if (user?.language !== i18n.language) {
        const currentLanguage = LANGUAGES.find((item) => item.lang === i18n.language);

        if (currentLanguage) {
          await updateLanguage({ language: currentLanguage.lang });
        }
      }
    } catch (err) {
      handleFetchError(err);
    }
  };

  const handleLanguageChange = async (value: string) => {
    await i18n.changeLanguage(value);
    await form.trigger();
  };

  return {
    handleLoginSubmit,
    handleLanguageChange,
    isLoggingIn,
    form,
    loginMethod,
    setLoginMethod,
  };
};
